import { useState, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useProfile } from '@/features/settings/hooks/use-profile'
import { useItemsPerPage } from '@/shared/hooks/use-items-per-page'
import { useToast } from '@/shared/components/ui/toast'
import { useAuthStore } from '@/shared/stores/auth.store'
import { useAccess, type QueueBucket } from '@/shared/rbac/use-access'
import { workspaceApi } from '../api/workspace.api'
import { exportToExcel } from '../utils/export-excel'
import { parseQueueBucketParam } from '../utils/status-map'
import { useQueueContextStore, type QueryBucketFilter } from '../stores/queue-context.store'
import { usePipelineHandoffStore, type PipelineHandoffPayload } from '@/shared/stores/pipeline-handoff.store'
import type { QueueFilters, QueueSortField, SortOrder, QueueViewMode } from '../types'

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

export const COLUMN_DEFS = [
  { key: 'claim_id', label: 'Claim ID' },
  // Platform tracking id (not PHI). Available to every role. Sits right after
  // Claim ID and is shown by default.
  { key: 'med_id', label: 'Med ID' },
  { key: 'dos', label: 'DOS' },
  { key: 'received_on', label: 'Received On' },
  { key: 'sla', label: 'SLA' },
  { key: 'source', label: 'Source' },
  { key: 'provider', label: 'Provider' },
  { key: 'specialty', label: 'Specialty' },
  { key: 'facility', label: 'Facility' },
  { key: 'payer', label: 'Payer' },
  // Insurance category of the primary payer (per-encounter override, else the
  // payer's default). Hidden by default — opt in via the column chooser.
  { key: 'insurance_category', label: 'Insurance Category' },
  { key: 'report_type', label: 'Report Type' },
  // Copilot / Autopilot (+ "(Edited)"). Blank on Received rows (no coding_mode
  // yet), so safe to keep visible across buckets — mirrors the history export.
  { key: 'coding_mode', label: 'Coding Mode' },
  // Query Type + Query Reason are a paired duo, only meaningful for rows in
  // the Query bucket. Both render blank when the row has no `queries`
  // summary, so they're safe to leave on for non-Query buckets too. We
  // place Type first so the coder reads category → actual question
  // left-to-right (matches the bucket's "what kind?" → "what is it?" flow).
  { key: 'query_type', label: 'Query Type' },
  { key: 'query_reason', label: 'Query Reason' },
  { key: 'confidence', label: 'Confidence' },
  // Code columns are hidden by default (see DEFAULT_HIDDEN_COLUMN_KEYS below).
  // CPT/HCPCS are bundled into one column to match how coders read them.
  { key: 'predicted_cpt_hcpcs', label: 'Predicted CPT/HCPCS' },
  { key: 'predicted_icd', label: 'Predicted ICD' },
  { key: 'predicted_modifiers', label: 'Predicted Modifiers' },
  { key: 'selected_cpt_hcpcs', label: 'Selected CPT/HCPCS' },
  { key: 'selected_icd', label: 'Selected ICD' },
  { key: 'selected_modifiers', label: 'Selected Modifiers' },
  { key: 'selected_cpt_icd_links', label: 'CPT-ICD Linking' },
  { key: 'selected_cpt_modifier_links', label: 'CPT-Modifier Linking' },
  { key: 'selected_hcpcs_icd_links', label: 'HCPCS-ICD Linking' },
  { key: 'selected_hcpcs_modifier_links', label: 'HCPCS-Modifier Linking' },
  // Combined CPT/HCPCS-Modifier-ICD summary in one column (client requirement),
  // e.g. "62321-59-G89.3". Same source rules as the linking columns above.
  { key: 'selected_final_summary', label: 'Final Summary' },
  // Per selected CPT/HCPCS with its ASC payment indicator, e.g. "45378-A2"
  // (blank PI when the code has no ASC indicator). Same source rules as above.
  { key: 'selected_cpt_pi_indicator', label: 'CPT-PI Indicator' },
  // Coding Complete tab only — reflects was_edited (Yes/No). Auto-hidden by
  // column-toggle filter on other buckets (see COLUMN_TOGGLE filter below).
  { key: 'is_edited', label: 'Is Edited' },
  // Coding Complete tab only — submitter + submission timestamp (from the
  // latest completed t_enc_reviews row).
  { key: 'completed_by', label: 'Completed By' },
  { key: 'completed_at', label: 'Completed At' },
  // Superadmin-only — applied audit flags and the modules they belong to.
  // queue.tsx hides these in the column-toggle for non-admin viewers and on
  // tabs other than Coding Complete; default hidden (opt-in via the toggle).
  // Module sits to the LEFT of Flags so the coder reads "category → specifics".
  { key: 'modules', label: 'Modules' },
  { key: 'flags', label: 'Flags' },
  // Latest non-system note on the encounter. Free-text + wide, so hidden by
  // default (opt-in via the column toggle); primarily used in the export.
  { key: 'notes', label: 'Notes' },
  { key: 'status', label: 'Status' },
] as const
export type ColumnKey = (typeof COLUMN_DEFS)[number]['key']

/**
 * Columns excluded from the default visible set. New coders should not see the
 * full code lists until they explicitly enable them via the column chooser.
 * Flags + Modules are also off by default — they're an opt-in superadmin tool.
 */
/**
 * Columns that only make sense once codes exist (submitted on Coding Complete,
 * or predicted on Copilot/Autopilot) — see `linkingColumnsActive` below. Shared
 * so every gate (ColumnToggle list, table visibility, export) stays in sync;
 * duplicating this list per call site is how a new linking column silently
 * misses one of the gates.
 */
export const LINKING_COLUMN_KEYS: ColumnKey[] = [
  'selected_cpt_icd_links',
  'selected_cpt_modifier_links',
  'selected_hcpcs_icd_links',
  'selected_hcpcs_modifier_links',
  'selected_final_summary',
  'selected_cpt_pi_indicator',
]

const DEFAULT_HIDDEN_COLUMN_KEYS = new Set<ColumnKey>([
  'source',
  'insurance_category',
  'predicted_cpt_hcpcs',
  'predicted_icd',
  'predicted_modifiers',
  'selected_cpt_hcpcs',
  'selected_icd',
  'selected_modifiers',
  'selected_cpt_icd_links',
  'selected_cpt_modifier_links',
  'selected_hcpcs_icd_links',
  'selected_hcpcs_modifier_links',
  'selected_final_summary',
  'selected_cpt_pi_indicator',
  'flags',
  'modules',
  'notes',
])

const DEFAULT_VISIBLE_COLUMN_KEYS: ColumnKey[] = COLUMN_DEFS
  .map((c) => c.key)
  .filter((k): k is ColumnKey => !DEFAULT_HIDDEN_COLUMN_KEYS.has(k))

export const DEFAULT_FILTERS: QueueFilters = {
  // Copilot is the default landing tab — toBackendStatuses() maps this to copilot_ready.
  status: ['copilot'],
  raw_status: null,
  specialty: null,
  payer: null,
  facility_id: null,
  provider_id: null,
  report_type_code: null,
  sla_status: null,
  confidence_min: null,
  confidence_max: null,
  date_from: null,
  date_to: null,
  received_from: null,
  received_to: null,
  assignment_status: null,
  search: '',
  client_id: null,
  coding_mode: null,
  flag_id: null,
  module_id: null,
}

/**
 * The sort fields the API accepts.
 *
 * `date_of_service` and `patient_name` were removed on 2026-08-10 — both are
 * encrypted columns now and SQL cannot order ciphertext (see
 * be/src/lib/crypto/phi-columns.ts).
 */
const ALLOWED_SORT_FIELDS: readonly QueueSortField[] = [
  'priority',
  'sla_deadline',
  'sla',
  'confidence',
  'created_at',
  'updated_at',
]

/**
 * Coerce a sort field to something the API still accepts, defaulting to SLA.
 *
 * This is load-bearing rather than defensive: sort preferences are persisted in
 * localStorage per user, so a browser that saved `date_of_service` before the
 * change would keep sending a value the API has dropped. Without this the queue
 * would fail (or silently fall back server-side) for every returning user.
 */
function sanitiseSortField(field: string | null | undefined): QueueSortField {
  return ALLOWED_SORT_FIELDS.includes(field as QueueSortField)
    ? (field as QueueSortField)
    : 'sla'
}

function hasValues(arr: unknown[] | null | undefined): boolean {
  return Array.isArray(arr) && arr.length > 0
}

const QUEUE_SESSION_KEY = 'medicodio:queue:session'
const QUEUE_PERSIST_PREFIX = 'nxtgen.queue.v'

/**
 * Wipe every persisted queue artifact — call from logout so the next sign-in
 * lands on the default tab (Copilot) instead of restoring the previous user's
 * last view. Touches in-memory snapshot, sessionStorage, and per-user
 * localStorage entries.
 */
export function clearQueuePersistedState() {
  try { sessionStorage.removeItem(QUEUE_SESSION_KEY) } catch { /* ignore */ }
  try {
    if (typeof window !== 'undefined') {
      const keys: string[] = []
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i)
        if (k && k.startsWith(QUEUE_PERSIST_PREFIX)) keys.push(k)
      }
      keys.forEach((k) => window.localStorage.removeItem(k))
    }
  } catch { /* ignore */ }
}

function readQueueSession(): { status: string[] | null; viewMode: QueueViewMode } | null {
  try {
    const raw = sessionStorage.getItem(QUEUE_SESSION_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function writeQueueSession(status: string[] | null, viewMode: QueueViewMode) {
  try {
    sessionStorage.setItem(QUEUE_SESSION_KEY, JSON.stringify({ status, viewMode }))
  } catch { /* ignore */ }
}

// ── Per-user persistence ────────────────────────────────────────────────────
// Filters, view mode, sort, page size, and visible columns survive refresh and
// re-login on the same browser. Keyed by user id so multiple users on one
// machine don't share state.

export interface PersistedQueueState {
  filters: QueueFilters
  viewMode: QueueViewMode
  sortField: QueueSortField
  sortOrder: SortOrder
  pageSize: number
  visibleColumns: ColumnKey[]
  /** User-chosen order of the reorderable middle columns. Sticky-left
   * (claim_id) and sticky-right (status) are always at the ends regardless. */
  columnOrder?: ColumnKey[]
  /** AUTO_ENABLE_COLUMNS keys already migrated in for this user (see
   * sanitizeVisibleColumns). Once a key lands here we stop force-re-adding
   * it, so a user who explicitly hides an auto-enabled column via the column
   * toggle stays hidden across reloads/logins instead of it reappearing. */
  autoEnabledColumns?: ColumnKey[]
  /** Column keys the user has ever explicitly flipped via the column toggle
   * (shown OR hidden), independent of autoEnabledColumns. This is what lets
   * sanitizeVisibleColumns tell "still visible only because of a stale old
   * auto-enable" apart from "user deliberately re-enabled this after hiding
   * it" — both look identical in autoEnabledColumns + visibleColumns alone,
   * since toggling a column never touched the auto-enable marker. A key here
   * is permanently exempt from the de-migration prune. */
  userToggledColumns?: ColumnKey[]
  /** REOFFER_COLUMNS keys already switched back on once for this user (see
   * reofferColumns). Separate from autoEnabledColumns because a re-offer
   * targets layouts that were ALREADY offered the column. */
  reofferedColumns?: ColumnKey[]
}

const PERSIST_VERSION = 1
const persistKey = (userId: string) => `nxtgen.queue.v${PERSIST_VERSION}.${userId}`

function loadPersisted(userId: string | null): Partial<PersistedQueueState> | null {
  if (!userId || typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(persistKey(userId))
    if (!raw) return null
    return JSON.parse(raw) as Partial<PersistedQueueState>
  } catch {
    return null
  }
}

function savePersisted(userId: string | null, state: PersistedQueueState): void {
  if (!userId || typeof window === 'undefined') return
  try {
    window.localStorage.setItem(persistKey(userId), JSON.stringify(state))
  } catch {
    // quota exceeded or storage disabled — silently ignore
  }
}

const VALID_COLUMN_KEYS = new Set(COLUMN_DEFS.map((c) => c.key)) as Set<string>

// Column keys that should be auto-enabled ONCE for users whose persisted
// column set predates the column's introduction. Add new keys here when you
// ship a new column you want on by default — sanitizeVisibleColumns unions it
// in the first time it sees a persisted state that doesn't yet know about it.
// `is_edited` is auto-enabled here even though it's only meaningful on
// Coding Complete; queue.tsx filters it out of the toggle + table for
// other buckets so users don't see it where it's irrelevant.
const AUTO_ENABLE_COLUMNS: ColumnKey[] = ['query_type', 'query_reason', 'is_edited', 'completed_by', 'completed_at', 'coding_mode', 'med_id']

/**
 * Filters a persisted visibleColumns array down to still-valid keys and
 * migrates in any AUTO_ENABLE_COLUMNS key not yet recorded in `alreadyOffered`
 * (the persisted autoEnabledColumns marker). A key already in `alreadyOffered`
 * is never force-re-added — that's what lets an explicit hide (via the column
 * toggle) survive reload/re-login instead of the column reappearing every time.
 *
 * The inverse also applies: a key recorded in `alreadyOffered` from a PRIOR
 * release but no longer in the current AUTO_ENABLE_COLUMNS (e.g. a column that
 * shipped default-on and was later switched to opt-in) is dropped from both
 * `visible` and `offered` here — UNLESS it's in `userTouched`. Without
 * `userTouched`, this can't tell "still visible only because of a stale old
 * auto-enable, never touched since" apart from "user explicitly hid it and
 * then deliberately re-enabled it" — both leave the key in `alreadyOffered`
 * AND in the saved visibleColumns, since toggling a column never updated the
 * auto-enable marker. `userTouched` is the caller's independent record of
 * "the user has explicitly flipped this key at some point"; anything in it
 * is permanently exempt from this prune, so a deliberate re-enable can never
 * be silently reversed by a later default-set change.
 *
 * Returns null when there's nothing usable to sanitize (caller substitutes
 * the all-defaults path).
 */
export function sanitizeVisibleColumns(
  keys: unknown,
  alreadyOffered: ReadonlySet<ColumnKey>,
  userTouched: ReadonlySet<ColumnKey>,
): { visible: Set<ColumnKey>; offered: Set<ColumnKey> } | null {
  if (!Array.isArray(keys)) return null
  const filtered = keys.filter((k): k is ColumnKey => typeof k === 'string' && VALID_COLUMN_KEYS.has(k))
  if (filtered.length === 0) return null
  const visible = new Set<ColumnKey>(filtered)
  const offered = new Set<ColumnKey>(alreadyOffered)
  for (const k of AUTO_ENABLE_COLUMNS) {
    if (!offered.has(k)) {
      visible.add(k)
      offered.add(k)
    }
  }
  for (const k of offered) {
    if (!AUTO_ENABLE_COLUMNS.includes(k) && !userTouched.has(k)) {
      visible.delete(k)
      offered.delete(k)
    }
  }
  return { visible, offered }
}

/**
 * Columns that were auto-enabled by an earlier release and have since moved
 * to opt-in. Used only to seed a pre-marker persisted state in
 * seedOfferedColumns below — never add to AUTO_ENABLE_COLUMNS itself.
 */
const FORMERLY_AUTO_ENABLED_COLUMNS: ColumnKey[] = ['selected_final_summary']

/**
 * Seeds the "already offered" set for a persisted state:
 * - No persisted state at all (brand-new user) — the all-defaults path
 *   (DEFAULT_VISIBLE_COLUMN_KEYS) already includes every AUTO_ENABLE_COLUMNS
 *   key, so treat them as pre-offered.
 * - Persisted state with an explicit autoEnabledColumns marker — trust it.
 * - Legacy persisted state that predates the marker itself — seed it with
 *   ONLY FORMERLY_AUTO_ENABLED_COLUMNS (never the current AUTO_ENABLE_COLUMNS
 *   — those must stay un-seeded so sanitizeVisibleColumns' union step can
 *   still migrate in a genuinely-missing current default for a truly old
 *   save). Without the FORMERLY_AUTO_ENABLED_COLUMNS seed, a browser whose
 *   visibleColumns already picked up a since-removed auto-enable column
 *   (saved back when it was still default-on, before marker-tracking
 *   existed) has no record proving it was auto-added, so the de-migration
 *   prune couldn't otherwise tell it apart from a deliberate choice and
 *   would leave it in place forever. Safe to seed even though we can't
 *   confirm it was actually auto-added: if it's not present in this user's
 *   visibleColumns, the prune is a no-op; if it IS present, either it really
 *   was auto-added (correct to prune) or the user deliberately re-enabled it
 *   (protected by `userTouched` regardless of this seed).
 */
export function seedOfferedColumns(persisted: Partial<PersistedQueueState> | null): Set<ColumnKey> {
  if (!persisted) return new Set(AUTO_ENABLE_COLUMNS)
  if (persisted.autoEnabledColumns) return new Set(persisted.autoEnabledColumns)
  return new Set(FORMERLY_AUTO_ENABLED_COLUMNS)
}

/**
 * Columns switched on ONCE more for every saved layout — even one that already
 * had them offered or hidden — because they used to be unavailable to some
 * roles. Recorded in reofferedColumns so it happens once; the user can hide the
 * column again afterwards and it stays hidden.
 * The marker lives in this browser's localStorage (nxtgen.queue.v1.<userId>),
 * so a new browser or cleared storage re-offers once.
 * med_id: was internal-only (read:all); every role sees it since 2026-11.
 */
const REOFFER_COLUMNS: ColumnKey[] = ['med_id']

export function reofferColumns(
  visible: ReadonlySet<ColumnKey>,
  alreadyReoffered: readonly ColumnKey[] | undefined,
): { visible: Set<ColumnKey>; reoffered: Set<ColumnKey> } {
  const out = new Set(visible)
  const reoffered = new Set<ColumnKey>(alreadyReoffered ?? [])
  for (const k of REOFFER_COLUMNS) {
    if (!reoffered.has(k)) {
      out.add(k)
      reoffered.add(k)
    }
  }
  return { visible: out, reoffered }
}

/** Default left-to-right order — matches the order columns are declared in
 * COLUMN_DEFS. The user can reorder via drag in the column chooser. */
export const DEFAULT_COLUMN_ORDER: ColumnKey[] = COLUMN_DEFS.map((c) => c.key)

/**
 * Validate a persisted column order against the current COLUMN_DEFS:
 * - Drops any unknown keys (e.g. a column that was removed)
 * - Appends any newly-introduced columns at the end (so users don't lose them
 *   after a deploy that adds a column)
 * Returns null when no usable persisted order was supplied so callers can
 * substitute DEFAULT_COLUMN_ORDER without juggling undefined.
 */
function sanitizeColumnOrder(keys: unknown): ColumnKey[] | null {
  if (!Array.isArray(keys)) return null
  const seen = new Set<ColumnKey>()
  const out: ColumnKey[] = []
  for (const k of keys) {
    if (typeof k === 'string' && VALID_COLUMN_KEYS.has(k) && !seen.has(k as ColumnKey)) {
      out.push(k as ColumnKey)
      seen.add(k as ColumnKey)
    }
  }
  if (out.length === 0) return null
  for (const k of DEFAULT_COLUMN_ORDER) {
    if (!seen.has(k)) out.push(k)
  }
  // Pin med_id immediately after claim_id so the column selector, drag order,
  // and table all show it beside Claim ID rather than trailing at the end
  // (older persisted orders appended it — see the append loop above).
  const mi = out.indexOf('med_id')
  if (mi >= 0) out.splice(mi, 1)
  const ci = out.indexOf('claim_id')
  out.splice(ci >= 0 ? ci + 1 : 0, 0, 'med_id')
  return out
}

/**
 * Drop status values the user no longer has bucket permission for. Returns
 * `null` (= unfiltered) if every requested bucket is hidden, so the caller
 * can substitute a sensible default (the first visible bucket).
 */
function sanitizeStatusForBuckets(
  status: string[] | null | undefined,
  visibleBuckets: Set<QueueBucket>,
): string[] | null {
  if (!status || status.length === 0) return null
  const kept = status.filter((s) => visibleBuckets.has(s as QueueBucket))
  return kept.length > 0 ? kept : null
}

function pickDefaultBucket(visibleBuckets: Set<QueueBucket>): QueueBucket {
  // Preferred order matches the visible tab strip in status-tabs.tsx so the
  // landing page always selects the left-most allowed bucket.
  const order: QueueBucket[] = ['copilot', 'received', 'autopilot', 'coding_complete', 'exported', 'query']
  for (const b of order) if (visibleBuckets.has(b)) return b
  return 'copilot'
}

export function useQueuePage() {
  const { data: profile } = useProfile()
  const { addToast } = useToast()
  const globalPageSize = useItemsPerPage()
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const { visibleBuckets } = useAccess()

  // Lazy init reads persisted state synchronously so the first render uses
  // the user's last-seen filters instead of flashing defaults then re-rendering.
  const persistedInit = loadPersisted(userId)

  const session = readQueueSession()

  // Deep-link filters from the global search drill-through (e.g. clicking a
  // "Patient" / "Provider" result jumps to /workspace/queue?patient_uuid=…).
  // These are single-entity navs that stay URL-encoded so the link is
  // shareable. clearAllFilters strips them when the user resets.
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkPatientUuid = searchParams.get('patient_uuid') || undefined
  const deepLinkProviderId = searchParams.get('provider_id') || undefined
  const deepLinkFacilityId = searchParams.get('facility_id') || undefined
  /** Set by global search drill-through — selects the correct queue tab. */
  const deepLinkBucketParam = searchParams.get('bucket')
  const deepLinkBucket = parseQueueBucketParam(deepLinkBucketParam)

  // Pipeline → queue handoff: a sessionStorage-backed store carries the
  // bucket + scope filter set when the coder clicks a bar on
  // /client-admin/pipeline. Read once at mount and clear so subsequent
  // re-mounts / back-button events don't replay the same scope over the
  // user's later edits.
  const initialHandoffRef = useRef<PipelineHandoffPayload | null | undefined>(undefined)
  if (initialHandoffRef.current === undefined) {
    const peek = usePipelineHandoffStore.getState().payload
    initialHandoffRef.current = (peek && peek.destination === 'queue')
      ? usePipelineHandoffStore.getState().consume()
      : null
  }
  const initialHandoff = initialHandoffRef.current

  // Read once at mount: if the coder is returning from an encounter detail
  // page, restore the bucket they were on. The snapshot stays in the store
  // until they leave the workspace area or the tab closes. A pipeline
  // handoff supersedes any restored snapshot — the user just acted from the
  // dashboard, so honour the latest intent.
  const initialSnapshot = initialHandoff ? null : useQueueContextStore.getState().snapshot
  const restoredFromSnapshotRef = useRef(!!initialSnapshot)
  const setSnapshot = useQueueContextStore((s) => s.setSnapshot)

  const [viewMode, setViewMode] = useState<QueueViewMode>(
    () => initialSnapshot?.viewMode ?? session?.viewMode ?? persistedInit?.viewMode ?? 'my'
  )
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set())
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false)
  const hasEntityDeepLink = !!(deepLinkPatientUuid || deepLinkProviderId || deepLinkFacilityId)

  // Build filter overrides from the handoff payload (replaces the previous
  // URL-param parsing). Pipeline's bucket comes via payload.bucket (no longer
  // ?bucket=…); pipeline's date range maps to Received On.
  const handoffBucket = initialHandoff?.bucket
    ? parseQueueBucketParam(initialHandoff.bucket)
    : null
  const hasPipelineHandoff = !!initialHandoff
  const pipelineDeepLinkOverrides: Partial<QueueFilters> = {}
  if (initialHandoff?.filters) {
    const f = initialHandoff.filters
    if (f.client_id && f.client_id.length > 0) pipelineDeepLinkOverrides.client_id = f.client_id
    if (f.facility_id && f.facility_id.length > 0) pipelineDeepLinkOverrides.facility_id = f.facility_id
    if (f.provider_id && f.provider_id.length > 0) pipelineDeepLinkOverrides.provider_id = f.provider_id
    if (f.specialty && f.specialty.length > 0) pipelineDeepLinkOverrides.specialty = f.specialty
    if (f.received_from) pipelineDeepLinkOverrides.received_from = f.received_from
    if (f.received_to) pipelineDeepLinkOverrides.received_to = f.received_to
  }

  const [filters, setFilters] = useState<QueueFilters>(() => {
    const base = initialSnapshot?.filters ?? {
      ...DEFAULT_FILTERS,
      ...(persistedInit?.filters ?? {}),
      ...(session ? { status: session.status } : {}),
      search: '',
    }
    // Global search drill-through: ?bucket= wins over snapshot/persisted tab.
    if (hasEntityDeepLink && deepLinkBucket && visibleBuckets.has(deepLinkBucket)) {
      return { ...base, status: [deepLinkBucket], search: '' }
    }
    // Pipeline handoff (sessionStorage payload, no URL params).
    if (hasPipelineHandoff) {
      const next: QueueFilters = { ...base, ...pipelineDeepLinkOverrides, search: '' }
      if (handoffBucket && visibleBuckets.has(handoffBucket)) {
        next.status = [handoffBucket]
      }
      return next
    }
    // If the user was on the Query bucket when they navigated away, the
    // snapshot has status=null on purpose (handleQueryBucketToggle clears it
    // so the BE returns the union of statuses that have open queries).
    // Don't fall back to a default bucket in that case — keep status=null so
    // the queryKey matches the cached Query-bucket entry.
    if (initialSnapshot?.queryBucketActive) {
      return { ...base, status: null }
    }
    // RBAC bucket sanitisation: drop any status value the user can no longer
    // see, falling back to the first allowed bucket so the queue isn't empty
    // because of a stale persisted filter.
    // Also: never start with a null/empty status — that surfaces every
    // encounter (including 'ingested' rows non-coders shouldn't see). Force a
    // default bucket so the page always lands on a single bucket view.
    const sanitised = sanitizeStatusForBuckets(base.status, visibleBuckets)
    if (sanitised && sanitised.length > 0) {
      if (sanitised === base.status) return base
      return { ...base, status: sanitised }
    }
    return { ...base, status: [pickDefaultBucket(visibleBuckets)] }
  })
  const [sortField, setSortField] = useState<QueueSortField>(
    // Default sort: SLA (2026-08-10). Was oldest-DOS-first, but date_of_service
    // is now an encrypted column and cannot be ordered in SQL.
    // sanitiseSortField() is essential here, not defensive: a browser that
    // persisted sortField='date_of_service' would otherwise keep sending a value
    // the API no longer accepts.
    () => sanitiseSortField(initialSnapshot?.sortField ?? persistedInit?.sortField)
  )
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    () => initialSnapshot?.sortOrder ?? persistedInit?.sortOrder ?? 'asc'
  )
  const restoredStack = initialSnapshot?.cursorStack && initialSnapshot.cursorStack.length > 0
    ? initialSnapshot.cursorStack
    : [undefined]
  const restoredPageIndex = Math.min(initialSnapshot?.pageIndex ?? 0, restoredStack.length - 1)
  const [cursor, setCursor] = useState<string | undefined>(restoredStack[restoredPageIndex])
  const [pageIndex, setPageIndex] = useState(restoredPageIndex)
  const [pageSize, setPageSize] = useState<number>(
    () => initialSnapshot?.pageSize ?? persistedInit?.pageSize ?? globalPageSize
  )
  const cursorStackRef = useRef<(string | undefined)[]>(restoredStack.slice(0, restoredPageIndex + 1))
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [debouncedSearch, setDebouncedSearch] = useState(initialSnapshot?.filters.search ?? '')
  // Computed once (via ref) so the visible/offered/userTouched initial states
  // all derive from the exact same sanitize pass.
  const initialColumnsRef = useRef<{ visible: Set<ColumnKey>; offered: Set<ColumnKey>; userTouched: Set<ColumnKey>; reoffered: Set<ColumnKey> } | null>(null)
  if (!initialColumnsRef.current) {
    const seed = seedOfferedColumns(persistedInit ?? null)
    const userTouched = new Set<ColumnKey>(persistedInit?.userToggledColumns ?? [])
    const sanitized = sanitizeVisibleColumns(persistedInit?.visibleColumns, seed, userTouched)
    initialColumnsRef.current = sanitized
      ? { ...reofferColumns(sanitized.visible, persistedInit?.reofferedColumns), offered: sanitized.offered, userTouched }
      : { visible: new Set(DEFAULT_VISIBLE_COLUMN_KEYS), offered: new Set(AUTO_ENABLE_COLUMNS), userTouched, reoffered: new Set(REOFFER_COLUMNS) }
  }
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(
    () => initialColumnsRef.current!.visible
  )
  const [autoEnabledColumns, setAutoEnabledColumns] = useState<Set<ColumnKey>>(
    () => initialColumnsRef.current!.offered
  )
  const [userToggledColumns, setUserToggledColumns] = useState<Set<ColumnKey>>(
    () => initialColumnsRef.current!.userTouched
  )
  const [reofferedColumns, setReofferedColumns] = useState<Set<ColumnKey>>(
    () => initialColumnsRef.current!.reoffered
  )
  // Wraps setVisibleColumns for the column-toggle UI: any key whose
  // membership actually changes is a deliberate user action, recorded in
  // userToggledColumns so it's permanently exempt from the auto-enable /
  // de-migration machinery in sanitizeVisibleColumns (see there for why that
  // record has to be independent of the autoEnabledColumns marker).
  const handleVisibleColumnsChange = useCallback((next: Set<ColumnKey>) => {
    const touched: ColumnKey[] = []
    next.forEach((k) => { if (!visibleColumns.has(k)) touched.push(k) })
    visibleColumns.forEach((k) => { if (!next.has(k)) touched.push(k) })
    setVisibleColumns(next)
    if (touched.length > 0) {
      setUserToggledColumns((prev) => {
        const out = new Set(prev)
        touched.forEach((k) => out.add(k))
        return out
      })
    }
  }, [visibleColumns])
  const [columnOrder, setColumnOrder] = useState<ColumnKey[]>(
    () => sanitizeColumnOrder(persistedInit?.columnOrder) ?? DEFAULT_COLUMN_ORDER
  )
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [queryBucketActive, setQueryBucketActive] = useState(
    (initialSnapshot?.queryBucketActive ?? false) && visibleBuckets.has('query'),
  )
  const [queryBucketFilter, setQueryBucketFilter] = useState<QueryBucketFilter>(
    initialSnapshot?.queryBucketFilter ?? 'all',
  )

  const isDeepLinked = hasEntityDeepLink

  // Apply ?bucket= from global search when landing on a deep-linked queue view.
  useEffect(() => {
    if (!hasEntityDeepLink || !deepLinkBucket || !visibleBuckets.has(deepLinkBucket)) return
    setQueryBucketActive(false)
    setFilters((prev) => {
      if (prev.status?.length === 1 && prev.status[0] === deepLinkBucket) return prev
      return { ...prev, status: [deepLinkBucket] }
    })
    writeQueueSession([deepLinkBucket], viewMode)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEntityDeepLink, deepLinkBucketParam])

  // Persist the bucket on every change so navigating to an encounter and back
  // restores the exact same view. Pagination is tracked here so the coder
  // returns to the same page they were on.
  useEffect(() => {
    setSnapshot({
      viewMode,
      filters,
      sortField,
      sortOrder,
      pageIndex,
      pageSize,
      queryBucketActive,
      queryBucketFilter,
      cursorStack: [...cursorStackRef.current],
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewMode,
    JSON.stringify(filters),
    sortField,
    sortOrder,
    pageIndex,
    pageSize,
    queryBucketActive,
    queryBucketFilter,
  ])

  // If userId arrives after first render (auth still resolving), hydrate then.
  const hydratedRef = useRef<string | null>(persistedInit ? userId : null)
  useEffect(() => {
    if (!userId || hydratedRef.current === userId) return
    const persisted = loadPersisted(userId)
    hydratedRef.current = userId
    if (!persisted) return
    if (persisted.filters) setFilters((f) => {
      const merged = { ...DEFAULT_FILTERS, ...persisted.filters, search: f.search }
      const sanitised = sanitizeStatusForBuckets(merged.status, visibleBuckets)
      if (sanitised === merged.status) return merged
      return { ...merged, status: sanitised ?? [pickDefaultBucket(visibleBuckets)] }
    })
    if (persisted.viewMode) setViewMode(persisted.viewMode)
    if (persisted.sortField) setSortField(persisted.sortField)
    if (persisted.sortOrder) setSortOrder(persisted.sortOrder)
    if (typeof persisted.pageSize === 'number') setPageSize(persisted.pageSize)
    const userTouched = new Set<ColumnKey>(persisted.userToggledColumns ?? [])
    const cols = sanitizeVisibleColumns(persisted.visibleColumns, seedOfferedColumns(persisted), userTouched)
    if (cols) {
      const re = reofferColumns(cols.visible, persisted.reofferedColumns)
      setVisibleColumns(re.visible)
      setAutoEnabledColumns(cols.offered)
      setReofferedColumns(re.reoffered)
    }
    setUserToggledColumns(userTouched)
    const order = sanitizeColumnOrder(persisted.columnOrder)
    if (order) setColumnOrder(order)
  }, [userId])

  // Persist on change. Search text is excluded so a refresh doesn't restore a
  // stale query, but every other knob survives.
  useEffect(() => {
    if (!userId) return
    savePersisted(userId, {
      filters: { ...filters, search: '' },
      viewMode,
      sortField,
      sortOrder,
      pageSize,
      visibleColumns: Array.from(visibleColumns),
      columnOrder,
      autoEnabledColumns: Array.from(autoEnabledColumns),
      userToggledColumns: Array.from(userToggledColumns),
      reofferedColumns: Array.from(reofferedColumns),
    })
  }, [userId, filters, viewMode, sortField, sortOrder, pageSize, visibleColumns, columnOrder, autoEnabledColumns, userToggledColumns, reofferedColumns])

  useEffect(() => {
    if (profile?.preferences?.items_per_page) {
      setPageSize(profile.preferences.items_per_page)
    }
  }, [profile?.preferences?.items_per_page])

  useEffect(() => {
    // Skip when filters.search already matches debouncedSearch — covers the
    // mount case (snapshot restored with both equal) and StrictMode's double
    // effect invoke. Without this guard the trailing setTimeout fires
    // resetPagination() after every remount and wipes restored pageIndex.
    if (filters.search === debouncedSearch) return
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search)
      resetPagination()
    }, 300)
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, debouncedSearch])

  // Clears the patient_uuid / provider_id / facility_id / bucket deep-link params
  // from the URL so the queue returns to the user's normal bucket view.
  const clearDeepLink = useCallback(() => {
    const next = new URLSearchParams(searchParams)
    next.delete('patient_uuid')
    next.delete('provider_id')
    next.delete('facility_id')
    next.delete('bucket')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])
  const serverParams = {
    limit: pageSize,
    cursor,
    // Always scope to the active bucket tab — charts must not appear in the
    // wrong queue (e.g. Copilot rows visible under Autopilot after global search).
    status: filters.status ?? undefined,
    // Raw `t_enc_encounters.status` filter — separate from the bucket token so
    // BE can apply it to aggregated tab counts too (so tab counts update when
    // user picks a raw status, matching how client/specialty already behave).
    raw_status: filters.raw_status ?? undefined,
    specialty: filters.specialty ?? undefined,
    facility_ids: filters.facility_id ?? undefined,
    payer: filters.payer ?? undefined,
    report_type_code: filters.report_type_code ?? undefined,
    sla_status: filters.sla_status ?? undefined,
    // BE accepts CSV → ANY(uuid[]) so multi-select propagates correctly.
    filter_client_id: filters.client_id && filters.client_id.length > 0 ? filters.client_id.join(',') : undefined,
    // Deep-link drill-throughs from the global search.
    filter_patient_uuid: deepLinkPatientUuid,
    // Drawer-selected providers take precedence; else deep-link drill-through.
    filter_provider_id: filters.provider_id && filters.provider_id.length > 0
      ? filters.provider_id.join(',')
      : (deepLinkProviderId ?? undefined),
    filter_facility_id: deepLinkFacilityId,
    confidence_min: filters.confidence_min ?? undefined,
    confidence_max: filters.confidence_max ?? undefined,
    date_from: filters.date_from ?? undefined,
    date_to: filters.date_to ?? undefined,
    received_from: filters.received_from ?? undefined,
    received_to: filters.received_to ?? undefined,
    assignment_status: filters.assignment_status ?? undefined,
    coding_mode: filters.coding_mode ?? undefined,
    search: debouncedSearch || undefined,
    sort_by: sanitiseSortField(sortField),
    sort_order: sortOrder,
    view_mode: viewMode,
    bucket: queryBucketActive ? ('query' as const) : undefined,
    query_filter: queryBucketActive ? queryBucketFilter : undefined,
    // Audit-flag filters (superadmin-only at the UI level). Sent as CSV to the
    // BE which parses them via csvToArray → uuid[].
    flag_id: filters.flag_id && filters.flag_id.length > 0 ? filters.flag_id.join(',') : undefined,
    module_id: filters.module_id && filters.module_id.length > 0 ? filters.module_id.join(',') : undefined,
  }

  function resetPagination() {
    setCursor(undefined)
    setPageIndex(0)
    cursorStackRef.current = [undefined]
    setSelectedRows(new Set())
  }

  function handleViewModeChange(mode: QueueViewMode) {
    setViewMode(mode)
    writeQueueSession(filters.status, mode)
    setSelectedRows(new Set())
    resetPagination()
  }

  function handleSort(field: QueueSortField) {
    if (field === sortField) {
      if (sortOrder === 'asc') {
        setSortOrder('desc')
      } else {
        setSortField('priority')
        setSortOrder('asc')
      }
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
    resetPagination()
  }

  function handleFiltersChange(newFilters: QueueFilters) {
    // Tab-specific default sort:
    //   • Coding Complete → newest submissions first (e.updated_at DESC)
    //   • Any other bucket → SLA order (was date_of_service ASC until 2026-08-10)
    // We override the sort on every tab switch so re-entering a tab
    // produces a predictable default. The user can still re-sort via
    // column headers — that sticks until they switch tabs again.
    const newBucket = newFilters.status?.length === 1 ? newFilters.status[0] : null
    const oldBucket = filters.status?.length === 1 ? filters.status[0] : null
    if (newBucket !== oldBucket) {
      if (newBucket === 'coding_complete') {
        setSortField('updated_at')
        setSortOrder('desc')
      } else if (oldBucket === 'coding_complete') {
        // Was date_of_service ASC (work-the-backlog); now SLA, since DOS is
        // encrypted and unorderable. ASC = least elapsed first.
        setSortField('sla')
        setSortOrder('asc')
      }
    }
    // Prune coding_mode values that aren't valid for the new bucket. Edited
    // variants only flip on submit, so they're meaningless pre-submit;
    // Received has no coding_mode at all. Without pruning, switching tabs
    // would carry an invisible filter and the user would see an empty list
    // with no obvious cause.
    let pruned = newFilters
    if (newFilters.coding_mode?.length) {
      if (newBucket === 'received') {
        pruned = { ...newFilters, coding_mode: null }
      } else if (newBucket !== 'coding_complete' && newBucket !== 'exported') {
        const kept = newFilters.coding_mode.filter((m) => m === 'autopilot' || m === 'copilot')
        pruned = { ...newFilters, coding_mode: kept.length ? kept : null }
      }
    }
    // raw_status persists across bucket switches — mirrors client/specialty/sla.
    // Tab counts naturally reflect it because BE applies raw_status to the
    // aggregated `status_counts` slice too (see queue.service.ts).
    setFilters(pruned)
    writeQueueSession(pruned.status, viewMode)
    resetPagination()
  }

  const goToNextPage = useCallback((nextCursor: string) => {
    // Synchronous double-fire guard: a rapid double-click (or two clicks before
    // React re-renders the disabled button) calls this twice in the same tick
    // with the SAME cursor (both read the current page's data). Without this the
    // stack pushes twice and pageIndex jumps +2 while the data only advances one
    // page — page number desyncs from the rows. The disabled attr isn't applied
    // synchronously, so it must be caught here.
    const stack = cursorStackRef.current
    if (stack[stack.length - 1] === nextCursor) return
    stack.push(nextCursor)
    setCursor(nextCursor)
    setPageIndex((i) => i + 1)
  }, [])

  const goToPrevPage = useCallback(() => {
    if (pageIndex <= 0) return
    cursorStackRef.current.pop()
    const prevCursor = cursorStackRef.current[cursorStackRef.current.length - 1]
    setCursor(prevCursor)
    setPageIndex((i) => i - 1)
  }, [pageIndex])

  /**
   * Walk every cursor from current page through to the final page and land
   * on it. Caller is responsible for resolving the chain (cursor pagination
   * has no random access) — it passes the ordered list of next-cursors it
   * fetched. We append them to the stack so Back behaves like the user
   * paged manually.
   */
  const goToLastPage = useCallback((cursorsAfterCurrent: string[]) => {
    if (cursorsAfterCurrent.length === 0) return
    for (const c of cursorsAfterCurrent) {
      cursorStackRef.current.push(c)
    }
    const finalCursor = cursorsAfterCurrent[cursorsAfterCurrent.length - 1]
    setCursor(finalCursor)
    setPageIndex((i) => i + cursorsAfterCurrent.length)
  }, [])

  function handleFirstPage() {
    resetPagination()
  }

  function handlePageSizeChange(size: number) {
    setPageSize(size)
    resetPagination()
  }

  function handleQueryBucketToggle(active: boolean) {
    setQueryBucketActive(active)
    setCursor(undefined)
    setPageIndex(0)
    if (active) handleFiltersChange({ ...filters, status: null })
  }

  function handleQueryBucketFilterChange(filter: QueryBucketFilter) {
    setQueryBucketFilter(filter)
    setCursor(undefined)
    setPageIndex(0)
  }

  const handleExport = useCallback(async (baseParams: Record<string, string | number>) => {
    if (exporting) return
    setExporting(true)
    addToast({
      title: 'Preparing export',
      description: 'Your Excel file will download shortly.',
      variant: 'info',
    })
    try {
      // Export exactly the currently-filtered list. `baseParams` is the same
      // canonical query the on-screen list uses (from useEncounterQueue); drop
      // pagination — the export endpoint forces its own row cap.
      const { limit: _limit, cursor: _cursor, ...exportParams } = baseParams
      void _limit; void _cursor
      const allData = await workspaceApi.getQueueExport(exportParams)
      // Reorder the canonical column defs to match the user's drag order, so
      // the XLSX header row mirrors what they see on screen. Unknown keys are
      // dropped by sanitizeColumnOrder during state init, so this is safe.
      const orderedCols = columnOrder
        .map((k) => COLUMN_DEFS.find((c) => c.key === k))
        .filter((c): c is (typeof COLUMN_DEFS)[number] => !!c)
      // Mirror queue.tsx's linkingColumnsActive gate: linking columns (incl.
      // Final Summary) only carry data on Coding Complete (submitted codes)
      // and Copilot/Autopilot (predicted codes) — everywhere else they're
      // hidden from the table/picker and must be excluded from the export too,
      // or a stale visibleColumns entry ships an unavailable, usually-empty column.
      const codingCompleteActive = !queryBucketActive
        && filters.status?.length === 1
        && filters.status[0] === 'coding_complete'
      const linkingColumnsActive = codingCompleteActive
        || (!queryBucketActive
          && filters.status?.length === 1
          && (filters.status[0] === 'copilot' || filters.status[0] === 'autopilot'))
      // query_* are auto-enabled but only shown on the Query bucket (queue.tsx
      // gates them on screen) — drop them here too or they leak into the export.
      const exportVisibleColumns = new Set(
        [...visibleColumns].filter(
          (k) =>
            (queryBucketActive || (k !== 'query_type' && k !== 'query_reason'))
            && (linkingColumnsActive || !LINKING_COLUMN_KEYS.includes(k)),
        ),
      )
      await exportToExcel(allData, exportVisibleColumns, orderedCols)
      addToast({
        title: 'Export ready',
        description: `Downloaded ${allData.length} encounter${allData.length === 1 ? '' : 's'}.`,
        variant: 'success',
      })
    } catch (err) {
      console.error('Export failed:', err)
      addToast({ title: 'Export failed', description: 'Please try again.', variant: 'error' })
    } finally {
      setExporting(false)
    }
  }, [exporting, visibleColumns, columnOrder, addToast, filters, queryBucketActive])

  // `status` is intentionally excluded — bucket tabs own that filter.
  // `raw_status` IS counted — it's a user-applied narrowing filter inside the bucket.
  const activeFilterCount = [
    hasValues(filters.specialty) ? 'x' : null,
    hasValues(filters.sla_status) ? 'x' : null,
    hasValues(filters.facility_id) ? 'x' : null,
    hasValues(filters.provider_id) ? 'x' : null,
    hasValues(filters.payer) ? 'x' : null,
    hasValues(filters.report_type_code) ? 'x' : null,
    filters.date_from || filters.date_to ? 'x' : null,
    filters.received_from || filters.received_to ? 'x' : null,
    filters.assignment_status,
    hasValues(filters.client_id) ? 'x' : null,
    hasValues(filters.coding_mode) ? 'x' : null,
    hasValues(filters.raw_status) ? 'x' : null,
    // Superadmin Flags / Modules filters (drawer). They narrow the list and are
    // sent to the server, so they must bump the filter-icon badge too — the
    // drawer's own "N active" count already includes them.
    hasValues(filters.flag_id) ? 'x' : null,
    hasValues(filters.module_id) ? 'x' : null,
  ].filter(Boolean).length

  return {
    viewMode, selectedRows, setSelectedRows,
    bulkAssignOpen, setBulkAssignOpen,
    filters, setFilters,
    sortField, sortOrder,
    pageIndex, pageSize,
    debouncedSearch,
    visibleColumns, setVisibleColumns, handleVisibleColumnsChange,
    columnOrder, setColumnOrder,
    filtersOpen, setFiltersOpen,
    exporting,
    queryBucketActive, queryBucketFilter,
    addToast,
    activeFilterCount, serverParams,
    deepLink: {
      isActive: isDeepLinked,
      patientUuid: deepLinkPatientUuid,
      providerId: deepLinkProviderId,
      facilityId: deepLinkFacilityId,
      clear: clearDeepLink,
    },
    resetPagination,
    handleViewModeChange, handleSort, handleFiltersChange,
    goToNextPage, goToPrevPage, goToLastPage, handleFirstPage, handlePageSizeChange,
    handleQueryBucketToggle, handleQueryBucketFilterChange, handleExport,
    /** True when this mount restored a queue snapshot (coder returning from
     * an encounter detail page). Lets the queue page skip the auto-switch
     * fallback so the user lands on the exact bucket they came from. */
    wasRestoredFromSnapshot: restoredFromSnapshotRef.current,
  }
}
